/**
 * The web addresses in a message: a URL format, never its meaning.
 *
 * Each whitespace-separated token is read on its own by the platform's URL
 * parser (`new URL`), after the punctuation a link sits in (brackets, quotes,
 * a sentence's full stop) and zero-width characters are taken off. A token
 * with no scheme is read as `https://`. It is a link when it parses as http(s)
 * with no user or password, and its host as typed is two or more labels of
 * letters, digits and hyphens whose last label is two or more lowercase
 * letters: "brand.co.il/landing", "shop.ch", "nba.com?ref=x". A sentence run
 * together after a full stop ("instead.It should") is prose: its last label
 * is not lowercase. There is no list of top-level domains.
 *
 * Nothing here reads the words around a link to decide what it is for (F15:
 * the `detectAssetIntent` regexes are deleted): the composer uses it to see
 * whether a message is nothing but links (`linksOnly`), and the builder to
 * read the link a model's typed part names.
 */

const ZERO_WIDTH = /[​-‍⁠﻿]/g;
const LEADING_WRAP = /^["'`(<[{]+/;
const TRAILING_WRAP = /["'`)>\]}.,;:!?]+$/;
const TYPED_HOST = /^(?:[A-Za-z0-9-]+\.)+([A-Za-z]+)$/;
const SCHEME = /^https?:\/\//i;
/** A token that only separates or numbers the links of a list. */
const LIST_MARK = /^(?:[-*•·–—>|,;/]+|\d{1,3}[.)]|[A-Za-z][.)])$/;

function tokensOf(message: string): Array<string> {
  return (message || "").replace(ZERO_WIDTH, "").split(/\s+/).filter(Boolean);
}

/** The link a token is, as `https://…` when it was typed without a scheme. */
function linkOf(token: string): string | null {
  const bare = token.replace(LEADING_WRAP, "").replace(TRAILING_WRAP, "");
  if (!bare) return null;
  const candidate = SCHEME.test(bare) ? bare : `https://${bare}`;
  const typedHost = candidate.replace(SCHEME, "").split(/[/?#:]/, 1)[0];
  const tld = TYPED_HOST.exec(typedHost)?.[1];
  if (!tld || tld.length < 2 || tld !== tld.toLowerCase()) return null;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.username || url.password) return null;
  } catch {
    return null;
  }
  return candidate;
}

/** The links in a message, in order, each once (letter case aside). */
export function messageLinks(message: string): Array<string> {
  const seen = new Set<string>();
  const links: Array<string> = [];
  for (const token of tokensOf(message)) {
    const link = linkOf(token);
    if (link && !seen.has(link.toLowerCase())) {
      seen.add(link.toLowerCase());
      links.push(link);
    }
  }
  return links;
}

/**
 * The links a message hands over on its own: its links when every token is a
 * link or a list's separator or number ("1.", "-", "•"), else none. Any other
 * token, in any script, makes it a message with words.
 */
export function linksOnly(message: string): Array<string> {
  const tokens = tokensOf(message);
  const allLinksOrMarks = tokens.every(
    (token) => LIST_MARK.test(token) || linkOf(token) !== null,
  );
  return allLinksOrMarks ? messageLinks(message) : [];
}
